import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { categoriesService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
    HiOutlineTag,
    HiOutlinePlus,
    HiOutlinePencil,
    HiOutlineTrash,
    HiOutlineX,
    HiOutlineCheckCircle,
    HiOutlineInformationCircle,
    HiOutlineFolderOpen
} from 'react-icons/hi';
import './EventsManage.css';

const CategoriesManage = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    useAuth();
    
    const [formData, setFormData] = useState({
        name_es: '',
        slug: '',
        description_es: '',
        is_active: true
    });

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const response = await categoriesService.getAll();
            setCategories(response.data);
        } catch (error) {
            toast.error('Error al cargar categorías');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Auto-generar slug cuando se escribe el nombre en español
        if (name === 'name_es') {
            const slug = value
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Remover acentos
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '');
            
            setFormData(prev => ({
                ...prev,
                name_es: value,
                slug: slug
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (editingCategory) {
                await categoriesService.update(editingCategory.id, formData);
                toast.success('Categoría actualizada exitosamente');
            } else {
                await categoriesService.create(formData);
                toast.success('Categoría creada exitosamente');
            }

            resetForm();
            loadCategories();
            setShowModal(false);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al guardar categoría');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            name_es: category.name_es,
            slug: category.slug,
            description_es: category.description_es || '',
            is_active: category.is_active
        });
        setShowModal(true);
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`¿Estás seguro de eliminar la categoría "${name}"?`)) {
            return;
        }

        try {
            await categoriesService.delete(id);
            toast.success('Categoría eliminada exitosamente');
            loadCategories();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al eliminar categoría');
            console.error(error);
        }
    };

    const resetForm = () => {
        setFormData({
            name_es: '',
            slug: '',
            description_es: '',
            is_active: true
        });
        setEditingCategory(null);
    };

    if (loading && categories.length === 0) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando categorías...</p>
            </div>
        );
    }

    return (
        <div className="content-manage">
            <div className="content-header">
                <div className="header-info">
                    <h1>Gestión de Categorías</h1>
                    <p>Organiza el contenido del blog con categorías</p>
                </div>
                <button className="btn btn-primary" onClick={() => {
                    resetForm();
                    setShowModal(true);
                }}>
                    <HiOutlinePlus />
                    <span>Nueva Categoría</span>
                </button>
            </div>

            {categories.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <HiOutlineTag />
                    </div>
                    <h3>No hay categorías</h3>
                    <p>Crea tu primera categoría para organizar los artículos del blog</p>
                    <button className="btn btn-primary" onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}>
                        <HiOutlinePlus />
                        <span>Crear Categoría</span>
                    </button>
                </div>
            ) : (
                <div className="categories-list">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Nombre (ES)</th>
                                    <th>Nombre (EN)</th>
                                    <th>Slug</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categories.map(category => (
                                    <tr key={category.id}>
                                        <td data-label="Nombre (ES)">
                                            <div className="table-cell-content">
                                                <HiOutlineTag style={{ marginRight: '8px', color: 'var(--primary)' }} />
                                                <strong>{category.name_es}</strong>
                                            </div>
                                        </td>
                                        <td data-label="Nombre (EN)">{category.name_en}</td>
                                        <td data-label="Slug"><code>{category.slug}</code></td>
                                        <td data-label="Estado">
                                            {category.is_active ? (
                                                <span className="badge badge-success">Activa</span>
                                            ) : (
                                                <span className="badge badge-error">Inactiva</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon btn-secondary"
                                                    onClick={() => handleEdit(category)}
                                                    title="Editar"
                                                >
                                                    <HiOutlinePencil />
                                                </button>
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={() => handleDelete(category.id, category.name_es)}
                                                    title="Eliminar"
                                                >
                                                    <HiOutlineTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">
                                <HiOutlineFolderOpen />
                                {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
                            </h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <HiOutlineX />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="alert-info">
                                    <HiOutlineInformationCircle />
                                    <span>Las categorías se traducen automáticamente al inglés</span>
                                </div>

                                <div className="grid grid-cols-2">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Nombre de la Categoría <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name_es"
                                            className="form-control"
                                            value={formData.name_es}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Noticias"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            Slug (URL amigable) <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="slug"
                                            className="form-control"
                                            value={formData.slug}
                                            onChange={handleInputChange}
                                            placeholder="Se genera automáticamente"
                                            required
                                        />
                                        <small className="form-hint">Se usa en las URLs: /blog/{formData.slug || 'noticias'}</small>
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label">Descripción</label>
                                        <textarea
                                            name="description_es"
                                            className="form-control"
                                            value={formData.description_es}
                                            onChange={handleInputChange}
                                            rows={3}
                                            placeholder="Describe esta categoría... (se traducirá automáticamente)"
                                        />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-check">
                                            <input
                                                type="checkbox"
                                                name="is_active"
                                                className="form-check-input"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                            />
                                            <span>Categoría activa (visible en el blog)</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    <HiOutlineCheckCircle />
                                    {loading ? 'Guardando...' : (editingCategory ? 'Actualizar' : 'Crear')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoriesManage;
